/**
 * useDemoFeed.ts
 *
 * The demo mode's READ-ONLY socket feeds (card D8b, plan §3.4): `demo:weather`
 * (the scripted day's weather — the animated sky and the weather widget),
 * `demo:people` (where the family is — the 3D avatar provider) and `demo:event`
 * (the run log).
 *
 * ONE listener set for the whole app, attached by `stores/socket.ts` beside the
 * demo clock's own (`useDemoClock.attachDemoClockListeners`), for the same
 * reason: a second `socket.on('demo:weather')` anywhere else is a second
 * subscription that outlives the hub it belonged to. The three feeds are one
 * module because they are one contract — the demo's read path — and because a
 * caller must be able to say "the demo is over" in one call (`resetDemoFeed`).
 *
 * Nothing here imports the API transport or a store (the socket store imports
 * THIS module on every boot, and `services/api.ts` reaches `stores/auth`): the
 * readers are pure (`utils/demoWeatherModel`, `utils/bimDemoPeople`) and the
 * log is a module-level list.
 *
 * A feed's state is module-level on purpose: `demo:people` arrives at 1 Hz
 * whether or not a 3D page is open, and the page that mounts later must paint
 * the family where the last frame put them rather than waiting a second.
 */
import { computed, shallowRef, type ComputedRef, type Ref } from 'vue';

import type { HubSocket } from '../stores/socket';
import { readDemoPeopleRows, type DemoPersonRow } from '../utils/bimDemoPeople';
import { demoWeatherOwnsSky, readDemoWeather, type DemoWeatherReading } from '../utils/demoWeatherModel';
import { useDemoClock } from './useDemoClock';
import { pushDemoRunLog } from './useDemoRunLog';

/** The last `demo:weather` frame, or `null` before the hub sends one. */
const weatherReading: Ref<DemoWeatherReading | null> = shallowRef(null);

/** The last `demo:people` frame — everyone the run placed, `[]` when nobody. */
const peopleRows: Ref<DemoPersonRow[]> = shallowRef([]);

/** When the last frame of each feed arrived (real ms) — a rig reads it for staleness. */
const weatherAt = shallowRef(0);
const peopleAt = shallowRef(0);

/** Adopt one `demo:weather` frame (a socket event, or a rig's replay of one). */
export function applyDemoWeatherEvent(payload: unknown): void {
  const reading = readDemoWeather(payload);
  if (!reading) return;
  weatherReading.value = reading;
  weatherAt.value = Date.now();
}

/** Adopt one `demo:people` frame. */
export function applyDemoPeopleEvent(payload: unknown): void {
  peopleRows.value = readDemoPeopleRows(payload);
  peopleAt.value = Date.now();
}

/** The demo is over (a disconnect, a hub switch, the mode turning off). */
export function resetDemoFeed(): void {
  weatherReading.value = null;
  peopleRows.value = [];
  weatherAt.value = 0;
  peopleAt.value = 0;
}

/** Subscribe a hub socket to the demo's read feeds. Returns the detach. */
export function attachDemoFeedListeners(socket: HubSocket): () => void {
  const onWeather = (payload: unknown): void => {
    applyDemoWeatherEvent(payload);
  };
  const onPeople = (payload: unknown): void => {
    applyDemoPeopleEvent(payload);
  };
  const onEvent = (payload: unknown): void => {
    // The hub's run-log line (plan §3.4: `{at, kind, label}`), published by the
    // hub's `DemoMoments` as the clock crosses a cut or the show changes day
    // (card `t_89e24d6b`). The app's own record of its presses is separate and
    // lands here too — one tab, both halves.
    pushDemoRunLog({ kind: 'event', payload });
  };

  socket.on('demo:weather', onWeather);
  socket.on('demo:people', onPeople);
  socket.on('demo:event', onEvent);

  return () => {
    socket.off('demo:weather', onWeather);
    socket.off('demo:people', onPeople);
    socket.off('demo:event', onEvent);
  };
}

/** What a surface reads: the reading itself, and whether it owns the sky. */
export interface DemoWeatherView {
  reading: ComputedRef<DemoWeatherReading | null>;
  /** The reading's own instant, as the sky's `at` — null when nothing arrived. */
  atMs: ComputedRef<number | null>;
  /** The condition code the sky draws, or '' (the real weather keeps its own). */
  condition: ComputedRef<string>;
  /** True only while demo mode is on AND a reading with a condition arrived. */
  ownsSky: ComputedRef<boolean>;
  /** When the last frame arrived (real ms; 0 = never). */
  receivedAt: ComputedRef<number>;
}

/**
 * The demo weather, as a surface reads it. Demo mode comes from `useDemoClock`
 * (the app-wide frame), so a reading can never outlive the mode that produced
 * it: switching the demo off puts every sky back on the real weather at once.
 */
export function useDemoWeather(): DemoWeatherView {
  const demo = useDemoClock();
  const enabled = computed(() => demo.enabled.value || demo.modeEnabled.value);
  return {
    reading: computed(() => weatherReading.value),
    atMs: computed(() => weatherReading.value?.simMs ?? null),
    condition: computed(() => (demoWeatherOwnsSky(weatherReading.value, enabled.value) ? weatherReading.value?.condition ?? '' : '')),
    ownsSky: computed(() => demoWeatherOwnsSky(weatherReading.value, enabled.value)),
    receivedAt: computed(() => weatherAt.value),
  };
}

/** What the avatar layer reads: the family's last published positions. */
export interface DemoPeopleView {
  rows: ComputedRef<DemoPersonRow[]>;
  receivedAt: ComputedRef<number>;
}

export function useDemoPeople(): DemoPeopleView {
  return {
    rows: computed(() => peopleRows.value),
    receivedAt: computed(() => peopleAt.value),
  };
}

/** The feeds' raw state — for a rig's debug hook; nothing in the app reads it. */
export function demoFeedDebug(): {
  weather: DemoWeatherReading | null;
  weatherAt: number;
  people: DemoPersonRow[];
  peopleAt: number;
} {
  return {
    weather: weatherReading.value,
    weatherAt: weatherAt.value,
    people: peopleRows.value,
    peopleAt: peopleAt.value,
  };
}
